import { ConfirmAction } from "@atlas/composite";
import { Badge, Button, Spinner, useToast } from "@atlas/core";
import { useTranslation } from "@atlas/sdk";
import { parseHubTokenKey } from "@shared";
import { Check, Copy } from "lucide-react";
import { useCallback, useMemo, useState } from "react";

export interface ExportTokenDialogProps {
  exportedKey: string | null;
  onClose: () => void;
  open?: boolean;
  title?: string;
  description?: string;
  isLoading?: boolean;
  error?: string | null;
}

export function ExportTokenDialog({
  exportedKey,
  onClose,
  open,
  title,
  description,
  isLoading,
  error,
}: ExportTokenDialogProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const [isCopied, setIsCopied] = useState(false);

  const decodedExportInfo = useMemo(
    () => parseHubTokenKey(exportedKey),
    [exportedKey],
  );

  const handleCopyKey = useCallback(() => {
    if (exportedKey) {
      navigator.clipboard.writeText(exportedKey);
      setIsCopied(true);
    }
  }, [exportedKey, toast, t]);

  return (
    <ConfirmAction
      open={open ?? (isLoading || exportedKey !== null)}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          onClose();
          setIsCopied(false);
        }
      }}
      title={
        <div className="flex items-center gap-2">
          {isLoading && <Spinner className="h-5 w-5" />}
          {isLoading
            ? t("hub.package_manager.exporting_title", {
                defaultValue: "Exporting...",
              })
            : error
              ? t("hub.package_manager.export_failed_title", {
                  defaultValue: "Export Failed",
                })
              : title ||
                t("hub.package_manager.export_key_title", {
                  defaultValue: "Export Successful",
                })}
        </div>
      }
      description={
        <div className="flex flex-col gap-4">
          <div>
            {isLoading
              ? t("hub.package_manager.exporting_description", {
                  defaultValue:
                    "Please wait while we generate your export token...",
                })
              : error
                ? error
                : description ||
                  t("hub.package_manager.export_key_description", {
                    defaultValue: "Please copy token key to import.",
                  })}
          </div>

          {!isLoading && !error && (
            <div className="flex items-center justify-start gap-4 py-2">
              {decodedExportInfo && (
                <Badge
                  variant="intelligence"
                  className="text-size-sm font-normal">
                  {decodedExportInfo.type === "packages"
                    ? t("hub.package_manager.export_count_info_packages", {
                        defaultValue: `${decodedExportInfo.count} Package(s) exported`,
                        count: decodedExportInfo.count,
                      })
                    : t("hub.package_manager.export_count_info_catalogs", {
                        defaultValue: `${decodedExportInfo.count} Catalog(s) exported`,
                        count: decodedExportInfo.count,
                      })}
                </Badge>
              )}

              <Button
                type="button"
                variant="link"
                onClick={handleCopyKey}
                disabled={!exportedKey}
                className="text-size-sm text-primary">
                {isCopied ? (
                  <>
                    <Check className="h-5 w-5 text-success-foreground" />
                    {t("hub.package_manager.token_copied", {
                      defaultValue: "Token copied!",
                    })}
                  </>
                ) : (
                  <>
                    <Copy className="h-5 w-5" />
                    {t("hub.package_manager.copy_token_key", {
                      defaultValue: "Copy token key",
                    })}
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      }
      cancelText={null}
      confirmText={t("hub.administer.close", { defaultValue: "Close" })}
      onConfirm={() => {
        onClose();
        setIsCopied(false);
      }}
      disabled={isLoading}
    />
  );
}
